import { VariantSelector } from "./variant-selector";
import { CLASS_LOW_STOCK, CLASS_SELECTED, CLASS_SOLD_OUT } from "../../tools/constants/classes";

export const SEL_SWATCH_CONTAINER = '[data-swatches]';
const SEL_SWATCH = '[data-swatch]';
const SEL_SWATCH_OBJECT = '[data-swatch-object]';
const SEL_SWATCH_DROPDOWN = '[data-swatch-dropdown]';
const SEL_SWATCH_VALUE = '[data-swatch-value]';
const SEL_LOW_STOCK_INDICATOR = '[data-low-stock-indicator]';
const SEL_STOCK_QUANTITY = '[data-stock-quantity]';
const SEL_LOW_STOCK_TEXT = '[data-low-stock-text]';
const SEL_SWATCH_TITLE = '[data-swatch-title]';

export const EVT_SOLD_OUT_STATUS_UPDATED = 'swatches:soldOutStatusUpdated';

const ATTR_POSITION = 'data-swatch-position';
const ATTR_VALUE = 'data-value';

export class Swatches extends VariantSelector {
  public readonly swatches:HTMLDivElement[];

  constructor(container:HTMLElement, updateHistory?:boolean) {
    super(container, updateHistory);
    this.swatches = Array.from(container.querySelectorAll<HTMLDivElement>(SEL_SWATCH).values());

    this.container.querySelectorAll<HTMLElement>(SEL_SWATCH_OBJECT).forEach(btn => {
      btn.addEventListener('click', this.onSwatchClick);
    });

    this.container.querySelectorAll<HTMLSelectElement>(SEL_SWATCH_DROPDOWN).forEach(dd => {
      dd.addEventListener('change', this.onDropdownChange)
    });

    this.onVariantSelected();
    this.updateSoldOutStatus();
  }

  private onSwatchClick = (e:MouseEvent) => {
    const btn = e.currentTarget as HTMLElement;
    
    const swatch = btn.closest(SEL_SWATCH);
    if(!swatch) return;

    const attrPosition = parseInt(swatch.getAttribute(ATTR_POSITION)||'');
    const attrValue = btn.getAttribute(ATTR_VALUE);
    if(isNaN(attrPosition) || !attrValue) return;

    this.setVariantOption(attrPosition - 1, attrValue);
  }

  private onDropdownChange = (e:Event) => {
    const sel = e.currentTarget as HTMLSelectElement;
    const swatch = sel.closest(SEL_SWATCH);
    if(!swatch) return;
    const attrPosition = parseInt(swatch.getAttribute(ATTR_POSITION)||'');
    if(isNaN(attrPosition)) return;
    this.setVariantOption(attrPosition - 1, sel.value);
  }

  public onVariantSelected() {
    const variant = this.getVariant();

    this.swatches.forEach(swatch => {
      const attrPosition = parseInt(swatch.getAttribute(ATTR_POSITION)||'');
      if(isNaN(attrPosition)) return;
      
      // Clear all selections if no variant is selected
      if (!variant) {
        const objs = swatch.querySelectorAll<HTMLElement>(SEL_SWATCH_OBJECT);
        objs.forEach(obj => {
          obj.classList.remove(CLASS_SELECTED);
          let parent = obj.parentElement;
          if(parent){
            parent.classList.remove(CLASS_SELECTED);
          }
        });

        const sels = swatch.querySelectorAll<HTMLSelectElement>(SEL_SWATCH_DROPDOWN);
        sels.forEach(sel => sel.value = '');

        swatch.querySelectorAll<HTMLElement>(SEL_SWATCH_VALUE).forEach(sv => {
          sv.textContent = '';
        });
        return;
      }

      const value = variant.options[attrPosition - 1];

      const objs = swatch.querySelectorAll<HTMLElement>(SEL_SWATCH_OBJECT);
      objs.forEach(obj => {
        const objVal = obj.getAttribute(ATTR_VALUE);
        if(objVal === value) {
          obj.classList.add(CLASS_SELECTED);
          let parent = obj.parentElement;
          if(parent){
            parent.classList.add(CLASS_SELECTED);
          }
    
        } else {
          obj.classList.remove(CLASS_SELECTED);
          let parent = obj.parentElement;
          if(parent){
            parent.classList.remove(CLASS_SELECTED);
          }
        }
      });

      const sels = swatch.querySelectorAll<HTMLSelectElement>(SEL_SWATCH_DROPDOWN);
      sels.forEach(sel => sel.value = value);

      swatch.querySelectorAll<HTMLElement>(SEL_SWATCH_VALUE).forEach(sv => {
        sv.textContent = value;
      });
    });

    this.updateSoldOutStatus();
    this.updateLowStockIndicator();
    this.updateSwatchTitles();
  }

  private updateSoldOutStatus() {
    this.swatches.forEach(swatch => {
      const attrPosition = parseInt(swatch.getAttribute(ATTR_POSITION)||'');
      if(isNaN(attrPosition)) return;

      const swatchObjects = swatch.querySelectorAll<HTMLElement>(SEL_SWATCH_OBJECT);
      swatchObjects.forEach(obj => {
        const objVal = obj.getAttribute(ATTR_VALUE);
        if(!objVal) return;

        const matchingVariants = this.data.variants.filter(variant => {
          const optionValue = variant.options[attrPosition - 1];
          return optionValue === objVal || optionValue === objVal.replace('-', ' ');
        });

        const isSoldOut = matchingVariants.length > 0 && matchingVariants.every(variant => {
          return !variant.available || 
                 (variant.inventory_management === 'shopify' && 
                  variant.inventory_policy === 'deny' && 
                  (variant.inventory_quantity || 0) <= 0);
        });

        if (isSoldOut) {
          obj.classList.add(CLASS_SOLD_OUT);
        } else {
          obj.classList.remove(CLASS_SOLD_OUT);
        }
      });
    });
    
    // Dispatch event to notify that sold out status has been updated
    this.dispatchSoldOutStatusUpdatedEvent();
  }

  private dispatchSoldOutStatusUpdatedEvent() {
    const event = new CustomEvent(EVT_SOLD_OUT_STATUS_UPDATED, {
      bubbles: true,
      detail: { 
        swatches: this,
        timestamp: Date.now()
      }
    });
    this.container.dispatchEvent(event);
  }

  private updateLowStockIndicator() {
    const variant = this.getVariant();
    const lowStockIndicator = this.container.querySelector(SEL_LOW_STOCK_INDICATOR) as HTMLElement;
    const lowStockTextElement = this.container.querySelector(SEL_LOW_STOCK_TEXT) as HTMLElement;

    if (!lowStockIndicator || !lowStockTextElement) return;

    // Hide indicator if no variant is selected
    if (!variant) {
      lowStockIndicator.style.display = 'none';
      lowStockTextElement.classList.remove(CLASS_LOW_STOCK);
      lowStockTextElement.classList.remove('in-stock');
      return;
    }

    if (this.areAllVariantsOutOfStock()) {
      lowStockIndicator.style.display = 'block';
      lowStockTextElement.textContent = 'out of stock';
      lowStockTextElement.classList.remove(CLASS_LOW_STOCK);
      lowStockTextElement.classList.remove('in-stock');
      return;
    }

    if (variant.inventory_management === 'shopify' && 
        variant.inventory_quantity !== null && 
        variant.inventory_quantity < 1) {
      
      lowStockIndicator.style.display = 'block';
      lowStockTextElement.textContent = 'out of stock';
      lowStockTextElement.classList.remove(CLASS_LOW_STOCK);
      lowStockTextElement.classList.remove('in-stock');
    } else if (variant.inventory_management === 'shopify' && 
               variant.inventory_quantity !== null && 
               variant.inventory_quantity < 5) {
      
      lowStockIndicator.style.display = 'block';
      lowStockTextElement.textContent = `Only ${variant.inventory_quantity} left`;
      lowStockTextElement.classList.add(CLASS_LOW_STOCK);
      lowStockTextElement.classList.remove('in-stock');
    } else if (variant.inventory_management === 'shopify' && 
               variant.inventory_quantity !== null && 
               variant.inventory_quantity > 5) {
      
      lowStockIndicator.style.display = 'block';
      lowStockTextElement.textContent = 'In Stock';
      lowStockTextElement.classList.remove(CLASS_LOW_STOCK);
      lowStockTextElement.classList.add('in-stock');
    } else {
      lowStockIndicator.style.display = 'none';
      lowStockTextElement.classList.remove(CLASS_LOW_STOCK);
      lowStockTextElement.classList.remove('in-stock');
    }
  }

  private updateSwatchTitles() {
    const swatchTitles = this.container.querySelectorAll<HTMLElement>(SEL_SWATCH_TITLE);
    const currentVariant = this.getVariant();

    swatchTitles.forEach(titleElement => {
      const optionName = titleElement.getAttribute('data-swatch-title');
      if (!optionName) return;

      const optionIndex = this.data.options.findIndex(option => 
        option.toLowerCase().replace(/\s+/g, '-') === optionName
      );

      if (optionIndex !== -1) {
        const originalOptionName = this.data.options[optionIndex];
        
        // Show "Select Size" when no variant is selected and this is the Size option
        if (!currentVariant && originalOptionName.toLowerCase() === 'size') {
          titleElement.textContent = 'Select Size';
        } else {
          titleElement.textContent = originalOptionName;
        }
      }
    });
  }

  private areAllVariantsOutOfStock(): boolean {
    return this.data.variants.every(variant => {
      return !variant.available || 
             (variant.inventory_management === 'shopify' && 
              variant.inventory_policy === 'deny' && 
              (variant.inventory_quantity || 0) <= 0);
    });
  }

  public dispose() {
    super.dispose();

    this.container.querySelectorAll<HTMLElement>(SEL_SWATCH_OBJECT).forEach(btn => {
      btn.removeEventListener('click', this.onSwatchClick);
    });

    this.container.querySelectorAll<HTMLSelectElement>(SEL_SWATCH_DROPDOWN).forEach(dd => {
      dd.removeEventListener('change', this.onDropdownChange);
    });
  }
}
